import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import BookingService from '../../../models/BookingService';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { buttonTheme, getPathObject, PageContainerActionParamType, pageToolbarDetailHeight, STATUS_BOOK_SERV_NEW_NAME } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import BookingServiceKendaraan from '../../../models/BookingServiceKendaraan';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import FormModal from '../../../components/FormModal';
import { Box, Button, ButtonGroup, createTheme, SvgIconOwnProps, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import { ButtonFormTheme, GridColorStyles } from '../../../expanded-theme';
import { v4 as uuidv4, validate as uuidValidate } from "uuid";
import NotificationDialog from '../../../components/NotificationDialog';
import GridViewEdit from '../../../components/GridViewEdit';
import { deepmerge } from '@mui/utils';
import Customer from '../../../models/Customer';
import { LookupSingleProp } from '../../../models/Lookup';
import moment from 'moment';
import Workorder from '../../../models/Workorder';

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Transaksi";
const code: MenuCode = "SERVICE-BOOKING";
const title: MenuTitle = "Booking Service";
const endpoint = "book";
const workorderEndpoint = "workorder";
    
const gridIconStyle : SvgIconOwnProps = {
    fontSize: "small",
    sx: {marginRight: "0.1rem", paddingBottom: 0},
};

const tipeKendaraanDataSource: LookupSingleProp[] = [
    {
        tipe: "Minibus",
    },
    {
        tipe: "Truk",
    },
    {
        tipe: "Sedan",
    },
];

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<BookingService>({tanggal_transaksi: moment().format("YYYY-MM-DD"), list_booking_service_kendaraan: []} as BookingService);
    const [initialBookingServiceKendaraanList, setInitialBookingServiceKendaraanList] = useState<BookingServiceKendaraan[]>([]);
    const [detailKendaraanToEdit, setDetailKendaraanToEdit] = useState<BookingServiceKendaraan | null>(null);
    const [bookingServiceKendaraanToView, setBookingServiceKendaraanToView] = useState<BookingServiceKendaraan | null>(null);
    const [workorderToView, setWorkorderToView] = useState<Workorder | null>(null);
    const [validationResult, setValidationResult] = useState<any[] | null>(null);
    const [currentTipeKendaraanDataSource, setCurrentTipeKendaraanDataSource] = useState<LookupSingleProp[]>(tipeKendaraanDataSource);
    const navigate = useNavigate();
    const customerRef = useRef();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
                const data: BookingService | null = response.data?.data ?
                {
                    ...response.data?.data,
                    customer: response.data?.data?.ucode_customer ?
                        {
                            ucode: response.data.data.ucode_customer,
                            kode: response.data.data.kode_customer,
                            nama: response.data.data.nama_customer,
                        }
                        :
                        undefined,
                    status: response.data?.data?.ucode_status ?
                        {
                            ucode: response.data.data.ucode_status,
                            kode: response.data.data.kode_status,
                            nama: response.data.data.nama_status,
                        }
                        :
                        undefined,
                }
                :
                null;

                setFormData(data ? {...data} : ({} as BookingService));
                const newTipeKendaraanDataSource = JSON.parse(JSON.stringify(currentTipeKendaraanDataSource));
                const newInitialBookingServiceKendaraan: BookingServiceKendaraan[] = [];

                data?.list_booking_service_kendaraan?.forEach(d => {
                    if (!newTipeKendaraanDataSource.filter((t: LookupSingleProp) => t.tipe === d.jenis_kendaraan).length)
                        newTipeKendaraanDataSource.push({
                            tipe: d.jenis_kendaraan
                        });

                    newInitialBookingServiceKendaraan.push(d);
                });

                setInitialBookingServiceKendaraanList(newInitialBookingServiceKendaraan);
                setCurrentTipeKendaraanDataSource(newTipeKendaraanDataSource);
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    useEffect(() => {
        if (!bookingServiceKendaraanToView || !apiCaller)
            return;

        setLoading(true);

        apiCaller.get(`${workorderEndpoint}/bookdetail/id?ucode=${bookingServiceKendaraanToView.ucode}`)
            .then(response => {
                if (!response.data?.data)
                    return;

                setWorkorderToView(response.data.data);
            })
            .catch(error => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [bookingServiceKendaraanToView]);

    useEffect(() => {
        if (!workorderToView)
            return;
        
        const pathObject = getPathObject(groupName, subgroupName, "SERVICE-WORKORDER");

        if (!pathObject?.mainPath || !pathObject?.formPath)
        {
            setWorkorderToView(null);
            return;
        }

        const dataToSend = {
            ...workorderToView,
            ucode_booking_service: formData?.ucode,
            no_booking_service: formData?.no_transaksi,
            tanggal_booking_service: formData?.tanggal_transaksi,
            nama_customer: formData?.nama_customer,
            nopol: workorderToView?.nopol,
            jenis_kendaraan: workorderToView?.jenis_kendaraan,
            keterangan_operator: formData?.keterangan_operator,
            list_workorder_brg: workorderToView.list_workorder_brg?.map(d => {
                return {
                    ...d,
                    brg: {
                        ucode: d.ucode_brg,
                        kode: d.kode_brg,
                        nama: d.nama_brg,
                    }
                }
            }),
            list_workorder_karyawan: workorderToView.list_workorder_karyawan?.map(d => {
                return {
                    ...d,
                    karyawan: {
                        ucode: d.ucode_karyawan,
                        kode: d.kode_karyawan,
                        nama: d.nama_karyawan,
                    }
                }
            }),
        };
        
        const parentPathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING");
        navigate(`${pathObject?.mainPath}${pathObject?.formPath}`, {state: {...dataToSend, isViewMode: true, parentUrl: `${parentPathObject?.mainPath}${parentPathObject?.formPath}`}});
    }, [workorderToView]);

    const onChanged = useCallback((valueObj: DataFormFieldChangeParamType<BookingService>) => {
        const {fieldName, value, keyFieldName, name, isObject} = valueObj;

        if (isObject && name && keyFieldName)
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : undefined,
                [name]: value,
            });
        
        if (!isObject)
            setFormData({
                ...formData,
                [fieldName]: value,
            });
    }, [formData]);

    const onDetailChanged = useCallback((detailData?: BookingServiceKendaraan) => {
        if (!detailData)
            return;

        const { list_booking_service_kendaraan } = formData;
        const current_list_booking_service_kendaraan = list_booking_service_kendaraan ?? [];

        if (detailData.jenis_kendaraan_obj)
        {
            detailData.jenis_kendaraan = detailData.jenis_kendaraan_obj.tipe;
            delete detailData.jenis_kendaraan_obj;
        }

        if (!detailData.ucode)
        {
            detailData.ucode = uuidv4();
            current_list_booking_service_kendaraan.push(detailData);
        }
        else {
            const currentDetail = current_list_booking_service_kendaraan.filter(d => d.ucode === detailData.ucode)[0];
            const currentIndex = currentDetail ? current_list_booking_service_kendaraan.indexOf(currentDetail) : -1;
            current_list_booking_service_kendaraan?.splice(currentIndex, 1, detailData);
        }

        const newData = {
            ...formData,
            list_booking_service_kendaraan: current_list_booking_service_kendaraan,
        };

        setFormData(JSON.parse(JSON.stringify(newData)));
    }, [formData, apiCaller]);

    const onValidate = useCallback((data: BookingService | undefined) => {
        if (!data?.tanggal_transaksi || !data?.ucode_customer)
            return false;

        if (data?.ucode && !data.no_transaksi)
            return false;

        return true;
    }, []);

    const onValidateDetailKendaraan = useCallback((detailToCheck: BookingServiceKendaraan, callback: Function) => {
        const existingData = formData.list_booking_service_kendaraan?.filter(d => d.nopol === detailToCheck.nopol && (detailToCheck.is_new || d.ucode !== detailToCheck.ucode));

        if (existingData?.length)
        {
            setValidationResult([
                {
                    type: "Error",
                    message: "Data kendaraan tidak boleh kembar.",
                }
            ]);

            return;
        }

        callback(detailToCheck);
    }, [formData]);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<BookingService> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const deletedBookingServiceKendaraanList: string[] = [];

        initialBookingServiceKendaraanList.forEach(b => {
            if (param?.data.list_booking_service_kendaraan?.filter(d => d.ucode === b.ucode)?.length || !b.ucode)
                return;

            deletedBookingServiceKendaraanList.push(b.ucode);
        });

        const dataToSend: BookingService = {
            ucode: param.data.ucode,
            no_transaksi: param.data.no_transaksi,
            tanggal_transaksi: moment(param.data.tanggal_transaksi).format("YYYY-MM-DD"),
            ucode_divisi: param.data.ucode_divisi,
            ucode_customer: param.data.ucode_customer,
            keterangan_operator: param.data.keterangan_operator,
        };

        const apiCallerObj = dataToSend.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(dataToSend)) : apiCaller.post(`${endpoint}`, JSON.stringify(dataToSend));

        apiCallerObj
            .then((response) => {
                const ucode_booking_service = response.data?.data?.ucode;
                const apiCalls: any[] = [];

                param?.data.list_booking_service_kendaraan?.forEach(d => {
                    const detailToSend: BookingServiceKendaraan =
                        d.is_new ?
                        {
                            jenis_kendaraan: d.jenis_kendaraan,
                            nopol: d.nopol,
                            ucode_booking_service
                        }
                        :
                        {
                            ucode: d.ucode,
                            jenis_kendaraan: d.jenis_kendaraan,
                            nopol: d.nopol,
                            ucode_booking_service
                        }
                    ;

                    d.is_new ? apiCalls.push(apiCaller.post(`${endpoint}/service-kendaraan`, JSON.stringify(detailToSend))) : apiCalls.push(apiCaller.put(`${endpoint}/service-kendaraan`, JSON.stringify(detailToSend)));
                });

                deletedBookingServiceKendaraanList.forEach(d => {
                    apiCalls.push(apiCaller.delete(`${endpoint}/service-kendaraan/${d}`));
                });

                Promise.all(apiCalls)
                    .then((responses) => {
                        setLoading(false);
        
                        if (param.callback)
                            param.callback();
                    })
                    .catch((error) => {
                        console.error({error});
                        setLoading(false);
                    })
                ;
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, initialBookingServiceKendaraanList]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<BookingService>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    isViewMode={isViewMode}
                    fields={[
                        {
                            name: "no_transaksi",
                            title: "No. Transaksi",
                            inputType: "text",
                            dataType: (typeof formData.no_transaksi),
                            isReadonly: true,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<BookingService>) => onChanged(valueObj),
                        },
                        {
                            name: "tanggal_transaksi",
                            title: "Tanggal Transaksi",
                            inputType: "date",
                            dataType: (typeof formData.tanggal_transaksi),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<BookingService>) => onChanged(valueObj),
                        },
                        {
                            name: "customer",
                            keyField: "ucode",
                            labelField: "nama",
                            editKeyField: "ucode_customer",
                            title: "Customer",
                            inputType: "combobox",
                            dataType: (typeof Customer),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<BookingService>) => onChanged(valueObj),
                            searchEndpoint: "customer/nama",
                            searchEndpointFilterParamName: "nama",
                            ref: customerRef,
                        },
                        {
                            name: "keterangan_operator",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<BookingService>) => onChanged(valueObj),
                            rows: 3,
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarDetailHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Kendaraan</Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                                <Tooltip
                                    title="Tambah Detail Barang"
                                    arrow
                                >
                                    <Button variant="contained" color="primary" size="small" onClick={() => setDetailKendaraanToEdit({ is_new: true })} disabled={isViewMode}>
                                        <AddIcon {...gridIconStyle} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </ThemeProvider>
                    </Box>
                </Box>
                <GridViewEdit<BookingServiceKendaraan>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: BookingServiceKendaraan) => setDetailKendaraanToEdit({
                                        ...rowData,
                                        jenis_kendaraan_obj: currentTipeKendaraanDataSource.filter(d => d.tipe === rowData.jenis_kendaraan)[0] ?? null
                                    }),
                                    icon: EditIcon,
                                    variant: "outlined",
                                    disabled: (rowData: BookingServiceKendaraan) => (rowData?.status_service_kendaraan && rowData.status_service_kendaraan !== STATUS_BOOK_SERV_NEW_NAME) || isViewMode,
                                },
                                {
                                    title: "Hapus",
                                    color: "delete",
                                    action: (rowData: BookingServiceKendaraan) => {
                                        const deletedDetailIndex = formData.list_booking_service_kendaraan?.findIndex(d => d.ucode === rowData.ucode) ?? -1;

                                        if (deletedDetailIndex < 0)
                                            return;

                                        formData.list_booking_service_kendaraan?.splice(deletedDetailIndex, 1);
                                        setFormData(JSON.parse(JSON.stringify(formData)));
                                    },
                                    icon: DeleteIcon,
                                    variant: "outlined",
                                    disabled: (rowData: BookingServiceKendaraan) => (rowData?.status_service_kendaraan && rowData.status_service_kendaraan !== STATUS_BOOK_SERV_NEW_NAME) || isViewMode,
                                },
                                {
                                    title: "Lihat Workorder",
                                    color: "dark",
                                    action: (rowData: BookingServiceKendaraan) => {
                                        setBookingServiceKendaraanToView(rowData);
                                    },
                                    icon: ManageAccountsIcon,
                                    variant: "outlined",
                                }
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "nopol",
                            columnTitle: "No. Polisi",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "jenis_kendaraan",
                            columnTitle: "Jenis Kendaraan",
                            columnType: "data",
                        },
                        {
                            columnName: "status_service_kendaraan",
                            columnTitle: "Status",
                            columnType: "data",
                        },
                    ]}
                    data={formData.list_booking_service_kendaraan}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
            </FormContainer>
        {
            detailKendaraanToEdit ?
            <FormModal<BookingServiceKendaraan>
                apiCaller={apiCaller}
                data={detailKendaraanToEdit}
                title={detailKendaraanToEdit.is_new ? "Tambah Detail Kendaraan" : "Ubah Detail Kendaraan"}
                fields={[
                    {
                        name: "nopol",
                        title: "No. Polisi",
                        inputType: "text",
                        dataType: (typeof detailKendaraanToEdit.nopol),
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 6,
                    },
                    {
                        name: "jenis_kendaraan_obj",
                        title: "Jenis Kendaraan",
                        inputType: "dropdown",
                        dataType: (typeof detailKendaraanToEdit.jenis_kendaraan_obj),
                        dataSource: currentTipeKendaraanDataSource,
                        keyField: "tipe",
                        labelField: "tipe",
                        editKeyField: "jenis_kendaraan",
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 6,
                    },
                ]}
                onClose={() => setDetailKendaraanToEdit(null)}
                onSave={(detailData?: BookingServiceKendaraan) => {
                    onDetailChanged(detailData);
                    setDetailKendaraanToEdit(null);
                }}
                onValidate={(detailToCheck: BookingServiceKendaraan, callback: Function) => {
                    onValidateDetailKendaraan(detailToCheck, callback);
                }}
            />
            :
            null
        }
        {
            validationResult?.length ?
            <NotificationDialog
                message={validationResult[0].message}
                onClose={() => setValidationResult(null)}
                title="Error!"
            />
            :
            null
        }
        </>
    );
};

export default Form;
